/**
 * Admin → Retention Policy
 *
 * Phase 5 (2026-05-22) - per-tenant DAM (ClickHouse activity_events)
 * retention. DPO + Super Admin can change; IT Admin reads only.
 *
 * Shows:
 *   • Current retention in days + human-readable (Y/M/D)
 *   • Bundle-recommended default + bounds
 *   • Slider [30, 3650]
 *   • Required reason text (audit-chain evidence)
 *   • Storage-impact estimate based on the bundle's default daily event rate
 *   • Manual "Run cleanup now" button (DPO + Super Admin)
 *
 * Every successful save emits an audit-chain event
 * `tenant.retention_policy.updated` on the backend so a CISO/auditor
 * can prove no one shortened retention to hide evidence.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  HardDrive,
  Loader2,
  RefreshCw,
  Save,
  Trash2,
  ShieldCheck,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  getRetentionPolicy,
  setRetentionPolicy,
  triggerRetentionCleanup,
  type RetentionPolicy,
  type RetentionCleanupResult,
} from '@/lib/api';

// ── Helpers ──────────────────────────────────────────────────────────

function daysToHuman(days: number): string {
  if (days >= 365) {
    const years = days / 365;
    return years === Math.round(years)
      ? `${Math.round(years)} year${years === 1 ? '' : 's'}`
      : `${years.toFixed(1)} years`;
  }
  if (days >= 30) {
    const months = Math.round(days / 30);
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Storage impact estimator. Uses the empirical 30 bytes/event compressed
 * size from ClickHouse (LowCardinality + ZSTD). Conservative event-rate
 * assumption for a mid-size BFSI tenant; the operator can adjust by
 * reading their actual rate from Activity → Stream.
 */
function estimateStorageGB(retentionDays: number, eventsPerDay: number): number {
  const bytesPerEvent = 30; // ZSTD-compressed in ClickHouse
  const totalBytes = retentionDays * eventsPerDay * bytesPerEvent;
  return totalBytes / 1024 / 1024 / 1024;
}

function formatGB(gb: number): string {
  if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${(gb * 1024).toFixed(0)} MB`;
}

// ── Page ─────────────────────────────────────────────────────────────

export function RetentionPolicy(): JSX.Element {
  const [policy, setPolicy] = useState<RetentionPolicy | null>(null);
  const [draft, setDraft] = useState<number>(2555);
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<RetentionCleanupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Event-rate slider for storage estimation (10M default, BFSI typical 50M)
  const [eventsPerDayMillions, setEventsPerDayMillions] = useState<number>(10);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await getRetentionPolicy();
      setPolicy(p);
      setDraft(p.retentionDays);
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const dirty = policy !== null && draft !== policy.retentionDays;

  const onSave = useCallback(async (): Promise<void> => {
    if (!dirty || !policy) return;
    const r = reason.trim();
    if (r.length < 4) {
      setError('Please enter a reason (at least 4 characters) - required for audit trail.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await setRetentionPolicy({ retentionDays: draft, reason: r });
      setPolicy(updated);
      setSavedAt(new Date());
      setReason('');
    } catch (err) {
      const msg = String((err as Error)?.message ?? err);
      // RBAM 403 - IT Admin trying to change
      if (msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('only dpo')) {
        setError(
          'Only DPO and Super Admin can change retention policy. You can view the current value but not change it.',
        );
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [dirty, policy, draft, reason]);

  const onTriggerCleanup = useCallback(async (): Promise<void> => {
    setCleanupRunning(true);
    setCleanupResult(null);
    setError(null);
    try {
      const result = await triggerRetentionCleanup();
      setCleanupResult(result);
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setCleanupRunning(false);
    }
  }, []);

  const estimatedCurrent = useMemo(() => {
    if (!policy) return 0;
    return estimateStorageGB(policy.retentionDays, eventsPerDayMillions * 1_000_000);
  }, [policy, eventsPerDayMillions]);

  const estimatedDraft = useMemo(() => {
    return estimateStorageGB(draft, eventsPerDayMillions * 1_000_000);
  }, [draft, eventsPerDayMillions]);

  const delta = estimatedDraft - estimatedCurrent;

  return (
    <PageContainer>
      <PageHeader
        title="DAM Retention Policy"
        subtitle="How long to keep ClickHouse activity_events for this workspace. BFSI/SEBI CSCRF requires 5-7 years; healthcare 6 years (HIPAA); telecom 2 years (TRAI). Default 7 years. Every change is recorded in the hash-chained audit log."
      />

      {error && (
        <div className="mb-4 rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
          <AlertTriangle className="-mt-0.5 mr-1 inline h-4 w-4" />
          {error}
        </div>
      )}

      {savedAt && !error && (
        <div className="mb-4 rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm text-emerald-500">
          <CheckCircle2 className="-mt-0.5 mr-1 inline h-4 w-4" />
          Retention policy updated at {savedAt.toLocaleTimeString()}. An audit-chain event was emitted; auditors can verify in the Egress Log.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <GlassCard>
          <div className="p-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Clock className="h-4 w-4" /> Current retention
            </div>
            <div className="text-2xl font-semibold tracking-tight">
              {loading || !policy ? <Loader2 className="h-6 w-6 animate-spin" /> : daysToHuman(policy.retentionDays)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {policy?.retentionDays} days
            </div>
          </div>
        </GlassCard>
        <GlassCard>
          <div className="p-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Database className="h-4 w-4" /> Allowed range
            </div>
            <div className="text-2xl font-semibold tracking-tight">
              {policy ? `${policy.minDays}-${policy.maxDays}` : '-'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              days. Lower bound: auditor minimum. Upper bound: ClickHouse table TTL ceiling.
            </div>
          </div>
        </GlassCard>
        <GlassCard>
          <div className="p-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <HardDrive className="h-4 w-4" /> Storage estimate
            </div>
            <div className="text-2xl font-semibold tracking-tight">
              {formatGB(estimatedCurrent)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              at {eventsPerDayMillions}M events/day. Adjust assumption below.
            </div>
          </div>
        </GlassCard>
      </div>

      <GlassCard>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Adjust retention</CardTitle>
              <CardDescription clamp>
                Slide to pick a new value. The save button requires a reason - it's written into the audit chain alongside the change.
              </CardDescription>
            </div>
            <Button size="sm" variant="ghost" onClick={refresh} disabled={loading || saving}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <div className="p-5 pt-0 space-y-5">
          {/* Retention slider */}
          <div>
            <label className="flex items-center justify-between text-sm font-medium mb-2">
              <span>Retention</span>
              <span className="font-mono text-base">
                {draft} days <span className="text-muted-foreground font-sans text-xs">({daysToHuman(draft)})</span>
              </span>
            </label>
            <input
              type="range"
              min={policy?.minDays ?? 30}
              max={policy?.maxDays ?? 3650}
              step={1}
              value={draft}
              onChange={(e) => setDraft(Number(e.target.value))}
              disabled={loading || saving || !policy}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-mono">
              <span>{policy?.minDays ?? 30}d</span>
              <span>1y</span>
              <span>3y</span>
              <span>5y</span>
              <span>7y</span>
              <span>10y</span>
            </div>

            {/* Quick presets */}
            <div className="flex flex-wrap gap-2 mt-3">
              {[
                { label: '1 year', days: 365, tag: 'general' },
                { label: '2 years', days: 730, tag: 'telecom TRAI' },
                { label: '3 years', days: 1095, tag: 'PCI DSS' },
                { label: '5 years', days: 1825, tag: 'ABDM' },
                { label: '6 years', days: 2190, tag: 'HIPAA' },
                { label: '7 years', days: 2555, tag: 'SEBI / IRDAI' },
                { label: '10 years', days: 3650, tag: 'RBI banks' },
              ].map((p) => (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => setDraft(p.days)}
                  disabled={loading || saving || !policy}
                  className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
                    draft === p.days
                      ? 'bg-primary/15 border-primary/40 text-primary'
                      : 'bg-background/40 border-border hover:bg-muted/40'
                  }`}
                >
                  {/* pointer-events-none on inner spans so any synthetic
                      click event (browser-MCP, e2e tools) lands on the
                      button itself, not on the span children. Real human
                      clicks bubble correctly either way; this just makes
                      automation reliable. */}
                  <span className="pointer-events-none">{p.label}</span>{' '}
                  <span className="pointer-events-none text-muted-foreground font-normal ml-1">{p.tag}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Storage impact */}
          <div className="rounded-md border border-border bg-background/40 p-4">
            <div className="flex items-center justify-between text-sm font-medium mb-2">
              <span>Storage impact at {eventsPerDayMillions}M events/day</span>
              <span className="font-mono text-xs">
                {formatGB(estimatedDraft)}{' '}
                {delta !== 0 && (
                  <Badge variant="outline" className={delta > 0 ? 'text-amber-500' : 'text-emerald-500'}>
                    {delta > 0 ? '+' : ''}{formatGB(Math.abs(delta))}
                  </Badge>
                )}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={200}
              step={1}
              value={eventsPerDayMillions}
              onChange={(e) => setEventsPerDayMillions(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-mono">
              <span>1M (small SaaS)</span>
              <span>50M (typical BFSI)</span>
              <span>200M (heavy trading)</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Estimate based on ~30 bytes per compressed event in ClickHouse (ZSTD + LowCardinality). For your real event rate, see Activity → Stream.
            </p>
          </div>

          {/* Reason text */}
          <div>
            <label htmlFor="reason" className="block text-sm font-medium mb-2">
              Reason for change <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading || saving || !dirty}
              placeholder="e.g. SEBI CSCRF audit prep - extending to 10 years per RBI Master Direction"
              className="w-full rounded-md border border-border bg-background/40 p-2 text-sm min-h-[60px]"
              maxLength={500}
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              {reason.length}/500 characters. Required for hash-chained audit trail. {reason.length > 0 && reason.length < 4 && '- minimum 4 characters'}
            </div>
          </div>

          {/* Save button */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <ShieldCheck className="h-3 w-3" />
              Only DPO and Super Admin can save changes. IT Admin can view.
            </div>
            <Button
              onClick={onSave}
              disabled={loading || saving || !dirty || reason.trim().length < 4}
              size="sm"
              data-action="disc.adm_retention.a01"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving…</>
              ) : (
                <><Save className="h-4 w-4 mr-1" /> Save retention policy</>
              )}
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* Manual cleanup trigger */}
      <GlassCard className="mt-6">
        <CardHeader>
          <CardTitle>Manual cleanup</CardTitle>
          <CardDescription>
            The nightly cron runs cleanup automatically (every 24h when{' '}
            <code className="text-xs">ENABLE_RETENTION_CLEANUP=true</code>). Trigger it now if you just shortened retention and want old events deleted immediately rather than waiting for the next nightly pass. ClickHouse executes the delete asynchronously after submission.
          </CardDescription>
        </CardHeader>
        <div className="p-5 pt-0 space-y-3">
          <Button onClick={onTriggerCleanup} disabled={cleanupRunning} variant="outline" size="sm" data-action="disc.adm_retention.a02">
            {cleanupRunning ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Running cleanup…</>
            ) : (
              <><Trash2 className="h-4 w-4 mr-1" /> Run cleanup now</>
            )}
          </Button>
          {cleanupResult && (
            <div className="rounded-md border border-border bg-background/40 p-3 text-xs font-mono">
              <div>Retention applied: {cleanupResult.retentionDays} days</div>
              <div>Rows marked for delete: {cleanupResult.rowsMarkedForDelete ?? '-'}</div>
              <div>Mutations submitted: {cleanupResult.mutationsSubmitted}</div>
              <div>Duration: {cleanupResult.durationMs} ms</div>
              {cleanupResult.errors.length > 0 && (
                <div className="text-red-500 mt-1">
                  Errors: {cleanupResult.errors.join('; ')}
                </div>
              )}
            </div>
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}

export default RetentionPolicy;
